import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios, { authHeaders } from "../../utils/axios";

export const fetchJantriTypes = createAsyncThunk(
  "jantri/fetchJantriTypes",
  async ({ is_active = true }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.get(
        `/jantri/jantri-types/all?is_active=${is_active}`,
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchJantriLocations = createAsyncThunk(
  "jantri/fetchJantriLocations",
  async (
    {
      jantri_type_id,
      parent_id = null,
      page = 1,
      limit = 10,
      direction = "forward",
      search = "",
      mode = "navigate",
    },
    { rejectWithValue }
  ) => {
    try {
      const auth = authHeaders();
      const res = await Axios.get("/jantri/jantri-locations", {
        params: {
          jantri_type_id,
          parent_id,
          page,
          limit,
          search, 
        },
        ...auth,
      });

      return {
        parent_id,
        direction,
        mode,
        response: res.data,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createJantriLocation = createAsyncThunk(
  "jantri/createJantriLocation",
  async ({ jantri_type_id, parent_id, name }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.post(
        "/jantri/jantri-locations",
        { jantri_type_id, parent_id, name },
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateJantriLocation = createAsyncThunk(
  "jantri/updateJantriLocation",
  async ({ id, name }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.patch(
        `/jantri/jantri-locations/${id}`,
        { name },
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateJantriStatus = createAsyncThunk(
  "jantri/updateJantriStatus",
  async ({ id, is_active }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.patch(
        `/jantri/jantri-locations/${id}`,
        { is_active },
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const reorderJantriLocations = createAsyncThunk(
  "jantri/reorderJantriLocations",
  async ({ id, new_sequence }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.patch(
        "/jantri/jantri-locations/reorder",
        { id, new_sequence },
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const createJantriFile = createAsyncThunk(
  "jantri/createJantriFile",
  async ({ jantri_type_id, location_id, file }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();

      const formData = new FormData();
      formData.append("jantri_type_id", jantri_type_id);
      formData.append("location_id", location_id);
      formData.append("file", file);

      const res = await Axios.post(
        "/jantri/jantri-files",
        formData,
        {
          ...auth,
          headers: {
            ...auth.headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const updateJantriFile = createAsyncThunk(
  "jantri/updateJantriFile",
  async ({ id, file }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();

      const formData = new FormData();
      formData.append("file", file);

      const res = await Axios.patch(
        `/jantri/jantri-files/${id}`,
        formData,
        {
          ...auth,
          headers: {
            ...auth.headers,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const reorderJantriFiles = createAsyncThunk(
  "jantri/reorderJantriFiles",
  async ({ id, new_sequence }, { rejectWithValue }) => {
    try {
      const auth = authHeaders();
      const res = await Axios.patch(
        "/jantri/jantri-files/reorder",
        { id, new_sequence },
        auth
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


const jantriSlice = createSlice({
  name: "jantri",
  initialState: {
    list: [],
    jantriList: {
    data: [],
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
      total_pages: 0,
    },
    next_level_label: null,
    type: null,
  },
    navStack: [],
    isLoading: false,
    error: null,
  },
  reducers: {
    popNavStack(state) {
      state.navStack.pop();
    },
    truncateNavStack(state, action) {
      const index = action.payload;
      state.navStack = state.navStack.slice(0, index + 1);
    },
    setNavStack(state, action) {
      state.navStack = action.payload || [];
    },
    resetJantriNavigation(state) {
      state.navStack = [];
      state.jantriList = {
        data: [],
        pagination: {
          page: 1,
          limit: 10,
          total: 0,
          total_pages: 0,
        },
        next_level_label: null,
        type: null,
        count: 0,
        parent_id: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchJantriTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchJantriTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.list = action.payload?.data || [];
      })
      .addCase(fetchJantriTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchJantriLocations.pending, (state) => {
            state.isLoading = true;
            state.error = null;
            })
      .addCase(fetchJantriLocations.fulfilled, (state, action) => {
        state.isLoading = false;
        const { parent_id, direction, mode } = action.payload;

        // 1. Check if we are returning to a level already in the breadcrumb trail
        const existingIndex = state.navStack.findIndex(
          (item) => item.parent_id === parent_id
        );

        if (existingIndex !== -1) {
          // If found, truncate the stack to that point
          state.navStack = state.navStack.slice(0, existingIndex);
        }
        // 2. Otherwise, if it's a forward navigation, push the level we are LEAVING
        // Safeguard: Don't push if we are staying on the same level or if no label exists
        else if (
          mode === "navigate" &&
          direction === "forward" &&
          state.jantriList.next_level_label &&
          state.jantriList.parent_id !== parent_id
        ) {
          state.navStack.push({
            label: state.jantriList.next_level_label,
            parent_id: state.jantriList.parent_id,
          });
        }

        state.jantriList = {
          data: action.payload.response.data.data || [],
          pagination: action.payload.response.data.pagination || {},
          next_level_label: action.payload.response.data.next_level_label || null,
          count: action.payload.response.data.total_root_locations,
          type: action.payload.response.data.type || null,
          parent_id,
        };
      })

      .addCase(fetchJantriLocations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});
export const { popNavStack, resetJantriNavigation, truncateNavStack, setNavStack } = jantriSlice.actions;
export default jantriSlice.reducer;
